import React, { isValidElement, JSX, useContext, useEffect, useState } from 'react'
import './index.scss'
import FormFieldProps, { FormFields, FormFieldType } from '@App/forms/v2/common/form-field'
import { addressFormKey, AddressType, isPostcodeRequiredCountry } from '@Entities/schemas/common/address-schema'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import { useFormContext } from 'react-hook-form'
import { isUSorCanada } from '@App/common/utils'
import { FormFieldTypes } from '@App/enum/form-field-types'
import { REQUIRED_MESSAGE } from '@Const/error-messages'
import DropdownFormFieldProps from '@App/forms/v2/common/dropdown-form-field'
import InnerForm from '@App/kit/fields/v2/InnerForm'
import { observer } from 'mobx-react'

interface MailingAddressProps {
    title?: string
    toolTip?: JSX.Element
    addressFormFields: FormFieldType<AddressType>
    subSection?: JSX.Element
    linkCityCountryAndAddressFields?: boolean
    formKey?: string
}

const MailingAddress = (props: MailingAddressProps) => {
    const { libStore } = useContext<RootStore>(StoresContext)
    const { watch, setValue, getValues } = useFormContext()
    const { title = 'Mailing Address', toolTip, subSection, formKey = addressFormKey } = props

    const addressFormFields = props.addressFormFields as FormFields<AddressType>

    const [countryId, setCountryId] = useState<DropdownFormFieldProps>(
        addressFormFields.countryId as DropdownFormFieldProps
    )
    const [state, setState] = useState<DropdownFormFieldProps>(addressFormFields.state as DropdownFormFieldProps)
    const [postalCode, setPostalCode] = useState<FormFieldProps>(addressFormFields.postalCode as FormFieldProps)

    const inputName = countryId.parentObjectName ? countryId.parentObjectName + '.' + countryId.name : countryId.name
    const countryValue = watch(inputName)

    const setOptions = () => {
        setCountryId({
            ...countryId,
            options: libStore.countries,
        })
    }

    const setOptionsForSelectedCountry = () => {
        if (isUSorCanada(countryValue) && !libStore.doesStateExistInCountry(countryValue, getValues(formKey)?.state)) {
            setValue(formKey, {
                ...getValues(formKey),
                state: '',
            })
        }
        setState({
            ...state,
            type: isUSorCanada(countryValue) ? FormFieldTypes.DROPDOWN : FormFieldTypes.TEXT,
            assistiveText: isUSorCanada(countryValue) ? REQUIRED_MESSAGE : '',
            options: libStore.statesForCountryForForm(countryValue),
        })

        setPostalCode({
            ...postalCode,
            assistiveText: isPostcodeRequiredCountry(countryValue) ? REQUIRED_MESSAGE : '',
        })
    }

    const fieldValidationDependencies = props.linkCityCountryAndAddressFields
        ? [
              {
                  dependencies: [addressFormFields.city, addressFormFields.countryId],
                  dependOn: addressFormFields.streetAddress,
              },
              {
                  dependencies: [addressFormFields.streetAddress, addressFormFields.countryId],
                  dependOn: addressFormFields.city,
              },
              {
                  dependencies: [addressFormFields.streetAddress, addressFormFields.city],
                  dependOn: addressFormFields.countryId,
              },
          ]
        : undefined

    useEffect(() => {
        setOptions()
    }, [])

    useEffect(() => {
        setOptionsForSelectedCountry()
    }, [countryValue])

    return (
        <div className="mailing-address-wrapper">
            <h5>
                {title}
                {isValidElement(toolTip) && toolTip}
            </h5>
            {subSection && subSection}
            <div className="mailing-address">
                <InnerForm
                    formFields={{
                        ...addressFormFields,
                        countryId,
                        state,
                        postalCode,
                    }}
                    fieldValidationDependencies={fieldValidationDependencies}
                />
            </div>
        </div>
    )
}

export default observer(MailingAddress)
